import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Input,
  IconButton,
  Text,
  Avatar,
  Button,
  useColorModeValue,
  Flex,
  useToast,
} from '@chakra-ui/react';
import { ArrowRightIcon } from '@chakra-ui/icons';
import { useAuth } from '../../contexts/AuthContext';
import { API_BASE_URL } from '../../services/api';

export type ChatMessage = {
  id: string;
  from: 'user' | 'bot';
  text: string;
  createdAt: string;
};

const ChatWindow: React.FC = () => {
  const { user } = useAuth();
  const toast = useToast();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const CHAT_API_ENDPOINT = `${API_BASE_URL}/chat`;

  // Fetch user's chat history from backend on mount
  useEffect(() => {
    const fetchChatHistory = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${CHAT_API_ENDPOINT}/history`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          setMessages(data.messages || []);
        }
      } catch (error) {
        console.error('Failed to fetch chat history:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChatHistory();
  }, [user]);

  useEffect(() => {
    // scroll to bottom
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async (messageText: string) => {
    if (!messageText.trim()) return;
    
    const token = localStorage.getItem('token');
    if (!token) {
      toast({
        title: 'Authentication required',
        description: 'Please log in to use the chat support.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    const userMsg: ChatMessage = {
      id: String(Date.now()) + '-u',
      from: 'user',
      text: messageText.trim(),
      createdAt: new Date().toISOString(),
    };
    setMessages(prev => [...prev, userMsg]);
    setText('');

    try {
      console.log('=== Sending message to:', CHAT_API_ENDPOINT);
      console.log('=== Message:', messageText.trim());
      
      const response = await fetch(CHAT_API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ message: messageText.trim() }),
      });

      console.log('=== Response received, status:', response.status);

      if (response.status === 401) {
        toast({
          title: 'Session expired',
          description: 'Please log in again to continue.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      if (!response.ok) {
        const errorText = await response.text();
        console.error('=== Error response:', errorText);
        throw new Error(`API returned ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      console.log('=== Success! Bot response:', data);
      
      const botMsg: ChatMessage = {
        id: String(Date.now()) + '-b',
        from: 'bot',
        text: data.response || "Sorry, I couldn't process that request.",
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, botMsg]);
    } catch (error) {
      console.error('=== Chat API error:', error);
      const errorMsg: ChatMessage = {
        id: String(Date.now()) + '-b',
        from: 'bot',
        text: "Sorry, I'm having trouble connecting. Please try again later.",
        createdAt: new Date().toISOString(),
      };
      setMessages(prev => [...prev, errorMsg]);
    }
  };

  const clearHistory = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    try {
      const response = await fetch(`${CHAT_API_ENDPOINT}/history`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setMessages([]);
        toast({
          title: 'Chat history cleared',
          status: 'success',
          duration: 2000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Failed to clear chat history:', error);
      toast({
        title: 'Failed to clear history',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const bg = useColorModeValue('white', 'gray.800');
  const userBg = useColorModeValue('green.500', 'green.600');
  const botBg = useColorModeValue('gray.100', 'gray.700');
  const headerBg = useColorModeValue('green.600', 'green.700');
  const inputBg = useColorModeValue('white', 'gray.700');
  const inputBorder = useColorModeValue('gray.300', 'gray.600');
  const userTextColor = useColorModeValue('white', 'white');
  const botTextColor = useColorModeValue('gray.800', 'white');

  return (
    <Box 
      w="full" 
      maxW="800px" 
      borderRadius="12px" 
      boxShadow="0 8px 24px rgba(0, 0, 0, 0.12)" 
      overflow="hidden" 
      bg={bg} 
      border="1px" 
      borderColor={useColorModeValue('gray.200', 'gray.700')}
    >
      <Flex 
        align="center" 
        justify="space-between" 
        px={4} 
        py={3} 
        bg="linear-gradient(135deg, #1a5e3f 0%, #2d7a52 100%)"
      >
        <HStack spacing={3}>
          <Avatar name="Support" size="sm" bg="white" color="green.600" />
          <VStack spacing={0} align="start">
            <Text fontWeight="bold" color="white">Chat Support</Text>
            <Text fontSize="sm" color="whiteAlpha.800">MarketLink Assistant</Text>
          </VStack>
        </HStack>
        <HStack spacing={2}>
          <Button size="sm" variant="ghost" onClick={clearHistory} color="white" _hover={{ bg: 'whiteAlpha.200' }}>Clear</Button>
        </HStack>
      </Flex>

      <Box h="480px" overflowY="auto" p={4} ref={scrollRef}>
        <VStack spacing={4} align="stretch">
          {loading ? (
            <Text color="gray.500">Loading your chat history...</Text>
          ) : messages.length === 0 ? (
            <Box>
              <Text color="gray.500" mb={2}>
                Welcome{user ? `, ${user.fullName}` : ''}! 👋
              </Text>
              <Text color="gray.500">
                I'm your MarketLink Assistant. Ask me about current market prices for agricultural products in Zimbabwe. 🌾
              </Text>
              <Text color="gray.500" fontSize="sm" mt={3}>
                Try asking: "What's the price of maize in Harare?" or "Compare wheat and rice prices"
              </Text>
            </Box>
          ) : null}

          {messages.map(msg => (
            <HStack key={msg.id} alignSelf={msg.from === 'user' ? 'flex-end' : 'flex-start'} maxW="80%">
              {msg.from === 'user' && <Avatar name={user?.fullName || 'User'} size="sm" bg="green.500" color="white" />}
              <Box px={4} py={3} borderRadius="lg" bg={msg.from === 'user' ? userBg : botBg} boxShadow="sm">
                <Text whiteSpace="pre-wrap" color={msg.from === 'user' ? userTextColor : botTextColor}>{msg.text}</Text>
                <Text fontSize="xs" color={msg.from === 'user' ? 'whiteAlpha.700' : 'gray.500'} mt={2}>{new Date(msg.createdAt).toLocaleTimeString()}</Text>
              </Box>
              {msg.from === 'user' && <Avatar name="You" size="sm" bg="green.600" color="white" />}
            </HStack>
          ))}
        </VStack>
      </Box>

      <HStack px={4} py={3} spacing={3} bg={headerBg} borderTop="1px" borderColor={useColorModeValue('gray.200', 'gray.700')}>
        <Input
          placeholder="Type your message..."
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') sendMessage(text);
          }}
          bg={inputBg}
          borderColor={inputBorder}
          _placeholder={{ color: useColorModeValue('gray.400', 'gray.500') }}
          _focus={{ borderColor: 'green.500', boxShadow: '0 0 0 1px var(--chakra-colors-green-500)' }}
          color={useColorModeValue('gray.800', 'white')}
        />
        <IconButton aria-label="Send" icon={<ArrowRightIcon />} colorScheme="green" onClick={() => sendMessage(text)} />
      </HStack>
    </Box>
  );
};

export default ChatWindow;
